import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id") || "";
  const [status, setStatus] = useState("checking");
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let ticks = 0;
    const t = setInterval(async () => {
      ticks += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setInfo(data);
          setStatus("paid");
          clearInterval(t);
        } else if (ticks > 15) {
          setStatus("pending");
          clearInterval(t);
        }
      } catch {
        if (ticks > 15) {
          setStatus("failed");
          clearInterval(t);
        }
      }
    }, 2000);
    return () => clearInterval(t);
  }, [sessionId]);

  return (
    <main className="min-h-screen bg-[#0F1012] grain" data-testid="payment-success-page">
      <header className="border-b border-[#2E3340]">
        <div className="max-w-6xl mx-auto px-6 h-20 flex items-center">
          <Logo />
        </div>
      </header>
      <div className="flex items-center justify-center px-6 py-20">
        <div className="max-w-lg w-full border border-[#2E3340] bg-[#1E2128] p-10 text-center">
          {status === "checking" && (
            <>
              <Loader2 className="mx-auto animate-spin text-amber-400" size={26} />
              <h1 className="font-serif-lux text-3xl text-amber-50 mt-6" data-testid="payment-status-heading">Confirming your payment…</h1>
              <p className="mt-3 text-sm text-zinc-400">One moment while Stripe settles the deposit.</p>
            </>
          )}
          {status === "paid" && (
            <>
              <span className="inline-flex items-center justify-center w-14 h-14 rounded-full border border-amber-500/40 mb-6">
                <Check className="text-amber-400" size={22} />
              </span>
              <p className="font-mono-lux text-[10px] font-semibold uppercase tracking-[0.35em] text-amber-500">
                {info?.kind === "balance" ? "Payment Received" : info?.kind === "voucher" ? "Voucher Issued" : "Deposit Received"}
              </p>
              <h1 className="font-serif-lux text-4xl text-amber-50 mt-3" data-testid="payment-status-heading">
                {info?.kind === "balance" ? "Journey settled." : info?.kind === "voucher" ? "A beautiful gift." : "Beautifully secured."}
              </h1>
              {info?.kind === "voucher" ? (
                <div className="mt-5">
                  <p className="text-sm text-zinc-300 leading-relaxed">
                    Your gift voucher for <span className="gold-text font-serif-lux text-xl" data-testid="payment-amount">${info?.amount}</span> is ready — it's also on its way to your inbox.
                  </p>
                  {info?.voucher_code && (
                    <p className="mt-4 border border-amber-500/40 bg-[#0F1012] px-6 py-4 font-mono-lux text-xl tracking-[0.2em] text-amber-300" data-testid="voucher-code">
                      {info.voucher_code}
                    </p>
                  )}
                  <p className="mt-3 text-xs text-zinc-500">Redeem it in the promo or gift code field when reserving — the fare reduces instantly.</p>
                </div>
              ) : (
                <p className="mt-4 text-sm text-zinc-300 leading-relaxed">
                  {info?.booking_reference && (
                    <>Reservation <span className="text-amber-300 font-mono-lux" data-testid="payment-booking-reference">{info.booking_reference}</span> — </>
                  )}
                  {info?.kind === "balance" ? (
                    <>payment of <span className="gold-text font-serif-lux text-xl" data-testid="payment-amount">${info?.amount}</span> received. Your reservation is now settled in full — thank you for riding with AUREUS.</>
                  ) : (
                    <>deposit of <span className="gold-text font-serif-lux text-xl" data-testid="payment-amount">${info?.amount}</span> received. The balance is settled after your journey.</>
                  )}
                </p>
              )}
              <p className="mt-2 text-xs text-zinc-500">A receipt from Stripe is on its way to your inbox.</p>
            </>
          )}
          {status === "pending" && (
            <>
              <h1 className="font-serif-lux text-3xl text-amber-50" data-testid="payment-status-heading">Payment processing.</h1>
              <p className="mt-3 text-sm text-zinc-400">Stripe is still confirming this payment — it usually completes within a minute.</p>
            </>
          )}
          {status === "failed" && (
            <>
              <h1 className="font-serif-lux text-3xl text-amber-50" data-testid="payment-status-heading">We couldn't confirm that payment.</h1>
              <p className="mt-3 text-sm text-zinc-400">If your card was charged, call the concierge at +1 (786) 874-0320 and we'll reconcile it immediately.</p>
            </>
          )}
          <Link
            to="/"
            data-testid="payment-home-link"
            className="inline-block mt-8 font-mono-lux text-[10px] font-semibold uppercase tracking-[0.3em] px-6 py-3 border border-amber-500/40 text-amber-300 hover:bg-amber-500 hover:text-black transition-colors"
          >
            Return Home
          </Link>
        </div>
      </div>
    </main>
  );
}
